/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { LearnerProfile_LearningGoal as LearningGoal } from '__generated__/graphql-types';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import indefiniteArticle from 'indefinite-article';

import { FormattedHTMLMessage, FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link } from '@coursera/cds-core';
import type { Typography2Variant } from '@coursera/cds-core';

import type { LearnerProfileRole } from 'bundles/page-config-common/contexts/consumer/LearnerProfileContext';
import { isPathwaysForAllEnabled } from 'bundles/page-config-common/utils/isPathwaysForAllEnabled';
import { RDP_URL } from 'bundles/unified-home-common/constants';

import _t from 'i18n!nls/consumer-home';

const styles = {
  careerRoleLink: css`
    &:hover {
      color: var(--cds-color-neutral-primary) !important;
    }

    color: var(--cds-color-neutral-primary) !important;
    font-weight: 600;
  `,
};

const createLink = (
  text?: string | null,
  url?: string,
  typographyVariant?: Extract<
    Typography2Variant,
    'subtitleLarge' | 'subtitleMedium' | 'bodyPrimary' | 'bodySecondary' | 'inherit'
  >,
  onRoleLinkClick?: () => void
) => {
  return (
    <Link
      typographyVariant={typographyVariant ?? 'subtitleMedium'}
      href={url}
      css={styles.careerRoleLink}
      enableVisitedState={false}
      onClick={onRoleLinkClick}
    >
      {text}
    </Link>
  );
};

export const formatCareerGoalAndRole = (roleName?: string, roleUrl?: string, onRoleLinkClick?: () => void) => {
  const roleLink = createLink(roleName, roleUrl, 'inherit', onRoleLinkClick);

  // Profile bar is GAd
  // So always show the generic message regardless of goal or shouldShowOnboardingV3
  return <FormattedMessage message={_t('Recommended for {roleLink} Roles')} roleLink={roleLink} />;
};

type FormatRoleAndSkillParams = {
  recommenderId: string;
  roleName?: string;
  roleUrl?: string;
  onRoleLinkClick?: () => void;
  skillOrCollectionTitle?: string;
  onlyCollectionTitle?: string;
};

// For Learner Object Pathway Recommender experiment
// https://tools.coursera.org/epic/experiment/-KhZcmZzEfCQYg6hR3Kfbw
export const formatRoleAndSkillExperimental = ({
  recommenderId,
  roleName,
  roleUrl,
  onRoleLinkClick,
  skillOrCollectionTitle,
  onlyCollectionTitle,
}: FormatRoleAndSkillParams) => {
  const roleLinkOrRoleName = roleUrl ? createLink(roleName, roleUrl, 'inherit', onRoleLinkClick) : roleName;
  const enablePathwaysForAll = isPathwaysForAllEnabled();

  switch (recommenderId) {
    case 'pathways-for-role-and-skills-complex-collection':
      return enablePathwaysForAll ? (
        <FormattedMessage
          message={_t('Essential skills to start your career as {article} {roleLinkOrRoleName}')}
          article={indefiniteArticle(roleName)}
          roleLinkOrRoleName={roleLinkOrRoleName}
        />
      ) : (
        <FormattedMessage
          message={_t('Essential skills for {roleLinkOrRoleName} roles')}
          roleLinkOrRoleName={roleLinkOrRoleName}
        />
      );
    case 'pathways-for-role-and-skill-filter':
      return (
        <FormattedMessage
          message={_t('Master {skill} to succeed as {article} {roleLinkOrRoleName}')}
          skill={skillOrCollectionTitle}
          article={indefiniteArticle(roleName)}
          roleLinkOrRoleName={roleLinkOrRoleName}
        />
      );
    case 'pathways-for-role-and-related-skills-complex-collection':
      return (
        <FormattedMessage
          message={_t('In-demand skills for {roleLinkOrRoleName} roles')}
          roleLinkOrRoleName={roleLinkOrRoleName}
        />
      );
    case 'pathways-for-skill-and-desired-role-filter':
      return (
        <FormattedMessage
          message={_t('Master {skill} to succeed as {article} {roleLinkOrRoleName}')}
          skill={skillOrCollectionTitle}
          article={indefiniteArticle(roleName)}
          roleLinkOrRoleName={roleLinkOrRoleName}
        />
      );
    case 'pathways-for-skill-and-desired-roles-complex-collection':
      return (
        <FormattedMessage message={_t('Master {skill} to succeed in these roles')} skill={skillOrCollectionTitle} />
      );
    case 'pathways-for-skills-complex-collection':
      return _t('Grow your skills');
    case 'pathways-for-skill':
      return onlyCollectionTitle || skillOrCollectionTitle;
    default:
      return _t('Recommended based on your interests');
  }
};

export const formatCareerGoal = (goal?: LearningGoal) => {
  switch (goal) {
    case LearningGoal.ChangeCareer:
      return _t('change roles');
    case LearningGoal.ImproveSkills:
      return _t('grow in your role');
    case LearningGoal.LandNewJob:
      return _t('start a career');
    default:
      return _t('explore topics outside of work');
  }
};

export const formatPersonalizedCareerGoal = (goal?: LearningGoal) => {
  switch (goal) {
    case LearningGoal.ChangeCareer:
      return _t('change your career');
    case LearningGoal.ImproveSkills:
      return _t('grow in your role');
    case LearningGoal.LandNewJob:
      return _t('start a career');
    default:
      return _t('explore topics outside of work');
  }
};

// Return the article (`a` or `an`) as a localized string.
export const getFormattedIndefiniteArticle = (phrase?: string | null) => {
  const article = indefiniteArticle(phrase);
  if (article === 'a') {
    return _t('a');
  }
  return _t('an');
};

export const getEditCareerGoalCopy = (
  goal?: LearningGoal,
  roles?: Array<LearnerProfileRole>,
  trackingFunc?: (roleSlug: string) => void,
  shouldShowOnboardingV3?: boolean
) => {
  const getRoleDisplay = (role: LearnerProfileRole) => {
    if (typeof role === 'string') {
      return role;
    } else {
      return createLink(role.name, `${RDP_URL}${role.slug}`, undefined, () => trackingFunc?.(role.slug));
    }
  };

  if (shouldShowOnboardingV3) {
    if (roles && roles.length > 0) {
      return (
        <FormattedMessage
          message={_t(
            "You're interested in {numberOfRoles, plural, =1 {{firstRole} roles} =2 {{firstRole} and {secondRole} roles} =3 {{firstRole}, {secondRole}, and {thirdRole} roles} other {{firstRole}, {secondRole}, {thirdRole} and more roles}}"
          )}
          numberOfRoles={roles.length}
          firstRole={roles[0] && getRoleDisplay(roles[0])}
          secondRole={roles[1] && getRoleDisplay(roles[1])}
          thirdRole={roles[2] && getRoleDisplay(roles[2])}
        />
      );
    } else return null;
  }

  if (goal && roles && roles.length > 0) {
    const formattedCareerGoal = formatCareerGoal(goal);

    return (
      <FormattedMessage
        message={_t(
          'Your career goal is to {formattedCareerGoal} {article} {numberOfRoles, plural, =1 {{firstRole}} =2 {{firstRole} or {secondRole}} =3 {{firstRole}, {secondRole}, or {thirdRole}} other {{firstRole}, {secondRole}, {thirdRole}, or {remainingRolesCount} more}}'
        )}
        formattedCareerGoal={`${formattedCareerGoal} ${goal === LearningGoal.ChangeCareer ? _t('to') : _t('as')}`}
        // "a" or "an"
        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        article={getFormattedIndefiniteArticle(typeof roles[0] === 'string' ? roles[0] : roles[0].name)}
        numberOfRoles={roles.length}
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        firstRole={getRoleDisplay(roles[0])}
        secondRole={roles[1] && getRoleDisplay(roles[1])}
        thirdRole={roles[2] && getRoleDisplay(roles[2])}
        remainingRolesCount={roles.length - 3}
      />
    );
  } else if (goal && !roles?.length) {
    const formattedCareerGoal = formatCareerGoal(goal);

    return (
      <FormattedHTMLMessage
        message={_t('Your career goal is to  <strong>{formattedCareerGoal}</strong>')}
        formattedCareerGoal={formattedCareerGoal}
      />
    );
  } else if (!goal && roles && roles.length > 0) {
    return (
      <FormattedMessage
        message={_t(
          'You are interested in the {numberOfRoles, plural, =1 {role of {firstRole}} =2 {roles of {firstRole} or {secondRole}} =3 {roles of {firstRole}, {secondRole}, or {thirdRole}} other {roles of {firstRole}, {secondRole}, {thirdRole}, or {remainingRolesCount} more}}'
        )}
        numberOfRoles={roles.length}
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        firstRole={getRoleDisplay(roles[0])}
        secondRole={roles[1] && getRoleDisplay(roles[1])}
        thirdRole={roles[2] && getRoleDisplay(roles[2])}
        remainingRolesCount={roles.length - 3}
      />
    );
  } else {
    return null;
  }
};

export const getPersonalizedEditCareerGoalCopy = (
  goal?: LearningGoal,
  roles?: Array<LearnerProfileRole>,
  trackingFunc?: (roleSlug: string) => void,
  shouldShowOnboardingV3?: boolean
) => {
  const getRoleDisplay = (role: LearnerProfileRole) => {
    if (typeof role === 'string') {
      return role;
    } else {
      return createLink(role.name, `${RDP_URL}${role.slug}`, undefined, () => trackingFunc?.(role.slug));
    }
  };

  if (shouldShowOnboardingV3) {
    if (roles && roles.length > 0) {
      return (
        <FormattedMessage
          message={_t(
            "You're interested in {numberOfRoles, plural, =1 {{firstRole} roles} =2 {{firstRole} and {secondRole} roles} =3 {{firstRole}, {secondRole}, and {thirdRole} roles} other {{firstRole}, {secondRole}, {thirdRole} and more roles}}"
          )}
          numberOfRoles={roles.length}
          firstRole={roles[0] && getRoleDisplay(roles[0])}
          secondRole={roles[1] && getRoleDisplay(roles[1])}
          thirdRole={roles[2] && getRoleDisplay(roles[2])}
        />
      );
    } else return null;
  }

  if (goal && roles && roles.length > 0) {
    if (roles.length >= 4) {
      const get4RoleMessage = (): string => {
        switch (goal) {
          case LearningGoal.LandNewJob:
            return _t('Your goal is to start a career in <u>multiple roles</u>');
          case LearningGoal.ChangeCareer:
            return _t('Your goal is to change your career to <u>multiple roles</u>');
          case LearningGoal.ImproveSkills:
            return _t('Your goal is to grow in your <u>multiple roles</u>');
          case LearningGoal.PersonalInterest:
          default:
            return _t('Your goal is to explore topics outside of work');
        }
      };

      return <FormattedHTMLMessage message={get4RoleMessage()} />;
    }

    const getLessThan4Message = (): string => {
      switch (goal) {
        case LearningGoal.LandNewJob:
          return _t(
            'Your goal is to start a career as {article} {numberOfRoles, plural, =1 {{firstRole}} =2 {{firstRole} or {secondRole}} =3 {{firstRole}, {secondRole}, or {thirdRole}} other {{firstRole}, {secondRole}, {thirdRole}, or {remainingRolesCount} more}}'
          );
        case LearningGoal.ChangeCareer:
          return _t(
            'Your goal is to change your career to {numberOfRoles, plural, =1 {{firstRole}} =2 {{firstRole} or {secondRole}} =3 {{firstRole}, {secondRole}, or {thirdRole}} other {{firstRole}, {secondRole}, {thirdRole}, or {remainingRolesCount} more}}'
          );
        case LearningGoal.ImproveSkills:
          return _t(
            'Your goal is to grow in your role as {article} {numberOfRoles, plural, =1 {{firstRole}} =2 {{firstRole} or {secondRole}} =3 {{firstRole}, {secondRole}, or {thirdRole}} other {{firstRole}, {secondRole}, {thirdRole}, or {remainingRolesCount} more}}'
          );
        case LearningGoal.PersonalInterest:
        default:
          return _t('Your goal is to explore topics outside of work');
      }
    };

    return (
      <FormattedMessage
        message={getLessThan4Message()}
        // "a" or "an"
        article={getFormattedIndefiniteArticle(typeof roles[0] === 'string' ? roles[0] : roles[0]?.name)}
        numberOfRoles={roles.length}
        firstRole={roles[0] && getRoleDisplay(roles[0])}
        secondRole={roles[1] && getRoleDisplay(roles[1])}
        thirdRole={roles[2] && getRoleDisplay(roles[2])}
        remainingRolesCount={roles.length - 3}
      />
    );
  } else if (goal && !roles?.length) {
    const formattedCareerGoal = formatPersonalizedCareerGoal(goal);

    return (
      <FormattedHTMLMessage
        message={_t('Your goal is to  <u>{formattedCareerGoal}</u>')}
        formattedCareerGoal={formattedCareerGoal}
      />
    );
  } else if (!goal && roles && roles.length > 0) {
    if (roles.length >= 4) {
      return <FormattedHTMLMessage message={_t('You are interested in <u>multiple roles</u>')} />;
    }

    return (
      <FormattedMessage
        message={_t(
          'You are interested in the {numberOfRoles, plural, =1 {role of {firstRole}} =2 {roles of {firstRole} or {secondRole}} =3 {roles of {firstRole}, {secondRole}, or {thirdRole}} other {roles of {firstRole}, {secondRole}, {thirdRole}, or {remainingRolesCount} more}}'
        )}
        numberOfRoles={roles.length}
        firstRole={roles[0] && getRoleDisplay(roles[0])}
        secondRole={roles[1] && getRoleDisplay(roles[1])}
        thirdRole={roles[2] && getRoleDisplay(roles[2])}
        remainingRolesCount={roles.length - 3}
      />
    );
  } else {
    return null;
  }
};
